import { Field, useFormikContext } from 'formik'
import * as React from 'react'
import type * as Api from 'src/api'
import * as Table from 'src/tailwind/components/Table'
import { CHOICE_INPUT_CLASS_NAME, gainedCreditsOf, type ChoicesFormValues, type EditableField } from './constants'

interface ChoiceCreditCellsProps {
  readonly choice: Api.AdministratorChoiceRow
  readonly isEditing: boolean
  readonly editableFields: readonly EditableField[]
  readonly formattedScore: string | number
}

/** The course credits, gained credits and score cells of a choices row; gained credits follow course credits. */
export function ChoiceCreditCells({
  choice,
  isEditing,
  editableFields,
  formattedScore,
}: ChoiceCreditCellsProps): JSX.Element {
  return (
    <>
      <Table.Td>
        {isEditing && editableFields.includes('couCredits') ? (
          <EditedCourseCredits choice={choice} />
        ) : (
          choice.courseCredits
        )}
      </Table.Td>
      <Table.Td>{isEditing ? <EditedGainedCredits choice={choice} /> : choice.credits}</Table.Td>
      <Table.Td>
        {isEditing && editableFields.includes('score') ? (
          <Field
            type="number"
            min={0}
            step="any"
            name={`rows.${choice.uid}.score`}
            className={CHOICE_INPUT_CLASS_NAME}
          />
        ) : (
          formattedScore
        )}
      </Table.Td>
    </>
  )
}

/** Editing course credits switches the row's gained credit over to following them. */
function EditedCourseCredits({ choice }: { readonly choice: Api.AdministratorChoiceRow }): JSX.Element {
  const { handleChange, setFieldValue } = useFormikContext<ChoicesFormValues>()

  const handleCourseCreditsChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event)
      void setFieldValue(`rows.${choice.uid}.creditsSynced`, true)
    },
    [choice.uid, handleChange, setFieldValue]
  )

  return (
    <Field
      type="number"
      min={0}
      step="any"
      name={`rows.${choice.uid}.couCredits`}
      className={CHOICE_INPUT_CLASS_NAME}
      onChange={handleCourseCreditsChange}
    />
  )
}

/** Reads the open row's values, so it only renders inside the editor's form — read-only tables have none. */
function EditedGainedCredits({ choice }: { readonly choice: Api.AdministratorChoiceRow }): JSX.Element {
  const { values } = useFormikContext<ChoicesFormValues>()
  return <>{gainedCreditsOf(choice, values.rows[choice.uid])}</>
}
